<script setup lang="ts">
  import { useCustomerOrderAbility, useCustomerAccepted } from '@storefront/storefront';
  import { useHttpClient, useAuthenticated, useRoute } from '@othercommerce/foundation';
  import type { ProductVariantResource, ProductVariantSimpleResource } from '@storefront/resources';
  import { computed } from 'vue';
  import { CustomerType } from '@othercommerce/domain';

  interface Props {
    variant: ProductVariantResource;
  }

  const props = defineProps<Props>();

  const route = useRoute();
  const http = useHttpClient();
  const canCustomerOrder = useCustomerOrderAbility();
  const isCustomerAccepted = useCustomerAccepted();
  const user = useAuthenticated();

  const disableMedicalProduct = computed(() => {
    return (user.value?.type !== CustomerType.MEDICAL && props.variant.isMedicinalProduct);
  });

  function onSelected(variant: ProductVariantSimpleResource) {
    http.dispatch('GET', route('web.product.show', { slug: variant.slug }), { preserveScroll: true, replace: true });
  }
</script>
<template>
  <div class="picker">
    <SfVariantPicker :variant @selected="onSelected" />
    <template v-if="canCustomerOrder">
      <SfProductCatalogPrice :item="variant" />
      <div class="picker__price">
        <SfProductPrice :price="variant" presentation />
        <SfProductOmnibus :price="variant" />
        <SfProductPackQuantity :product="variant" />
        <SfProductMultipackUnitPrice :price="variant" />
        <SfProductUnitPrice :price="variant" />
      </div>
      <div class="picker__annotations">
        <SfProductStock :variant />
        <SfProductPoints :variant />
        <SfProductRealizationTime :item="variant" />
      </div>
      <SfCartStoreButton :variant="variant" :disabled="disableMedicalProduct">
        <template #after v-if="variant">
          <SfIconCartAdd aria-hidden="true" />
        </template>
      </SfCartStoreButton>
    </template>
    <template v-else>
      <div class="picker__guest">
        <RouterLink v-if="isCustomerAccepted === undefined" :href="$route('web.account.session.create')" rel="nofollow">
          {{ $t('SHOP.COMMON.MESSAGE.SIGN_IN_TO_SEE_PRICES_NOTICE') }}
        </RouterLink>
        <span v-else>
          {{ $t('SHOP.PRICE.MESSAGE.ACCOUNT_NOT_ACTIVE') }}
        </span>
      </div>
    </template>
  </div>
</template>
<style scoped lang="scss">
  .picker {
    display: flex;
    flex-direction: column;
    gap: var(--gap-card);

    &__annotations,
    &__price {
      display: flex;
      flex-direction: column;
    }

    &__annotations {
      gap: 0.5rem;
    }

    &__price {
      gap: 0.125rem;
    }

    &__guest {
      color: var(--secondary-color);
      font-weight: var(--font-weight-bold);
      font-size: 0.875rem;
    }
  }
</style>
